import {useState, useEffect, useMemo} from 'react';
import {
  MoreVertical,
  Info,
  ChevronUp,
  ChevronDown,
  Filter,
  History,
  Trash2,
} from 'lucide-react';
import {Button} from '@/shared/ui/button';
import {
  useColumnPreferences,
  applyColumnVisibilityPrefs,
} from '@/shared/hooks/use-column-preferences';
import {useAuth} from '@/contexts/AuthContext';
import {useProjectRole} from '@/hooks/use-project-role';
import {getProjectPermissionFlags} from '@/shared/lib/project-permissions';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import {Badge} from '@/shared/ui/badge';
import {PageLoader} from '@/shared/ui/page-loader';
import {Avatar, AvatarFallback} from '@/shared/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
  PaginationNext,
  PaginationPrevious,
} from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {Input} from '@/shared/ui/input';
import ParticipantDetailsDialog from '@/shared/components/ParticipantDetailsDialog';
import ChangeStatusDialog from './ChangeStatusDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  formatStatusDisplay,
  getStatusColor,
} from '@/shared/lib/participant-schedule-status-display';
import {cn} from '@/lib/utils';
import {PARTICIPANT_ROSTER_DATA_TEXT_CLASS} from '@/utils/participantRosterTypography';
import {
  FAMILY_GRID_NOT_APPLICABLE,
  getParticipantTypeForDisplay,
  isSoloPrimaryNoFamilyMembers,
  normalizeRelationshipLabelForUi,
} from '@/utils/participantDisplay';
import {
  ColumnsSelectorPopover,
  EmptyValue,
  ExportButton,
  SortableHeaderCell,
  TableBodyState,
  TableSearchInput,
  TableToolbar,
} from '@/shared/components/table';
import type {ParticipantsExportContext} from '../utils/participantsGridExport';

export interface ParticipantsTableRow {
  id: string | number;
  name: string;
  email: string;
  phone?: string;
  gender: string;
  dob?: string;
  /** Derived project-level status card / change-status dialog (mapBackendStatusToUI). */
  status: string;
  /** Scheduler workflow field; same source as Scheduler grid Participant Status. */
  schedule_status?: string | null;
  /** Backend-computed status bucket shared with the KPI cards (single source of
   * truth for card counts + the card filter). See participantStatusBucketSql. */
  status_bucket?: string | null;
  joinDate?: string;
  /** Date the participant was endorsed (display-only column). */
  endorsed_date?: string | null;
  /** Raw API date (YYYY-MM-DD, etc.); supports search when display `dob` differs. */
  date_of_birth?: string | null;
  source?: string;
  sourceDetail?: string;
  notes?: string;
  /** OneForma / Event Hub family linkage (scheduler AC1/AC2). */
  participant_type?: string | null;
  oneforma_user_id?: string | number | null;
  oneforma_primary_user_id?: string | null;
  related_participant_ref?: string | null;
  relationship?: string | null;
  is_family_group?: boolean;
  family_relationship_type?: string | null;
  family_group_code?: string | null;
  role_in_family?: string | null;
  /** Per-project resource fields sourced from participant_demographic_answers. */
  resource_type?: string | null;
  agency_name?: string | null;
  created_at?: string;
  demographics_updated_on?: string | null;
}

interface DemographicQuestion {
  question_id: number;
  question: string;
  question_type: number;
  question_data: any;
  key: string;
  // True only for additional_data-derived (uploaded CSV) columns. Real
  // question_ids are backend-assigned bigints that can be arbitrarily large,
  // so this must never be inferred from question_id magnitude.
  isSynthetic?: boolean;
}

type ServerPagination = {
  page: number;
  pages: number;
  total: number;
  onPageChange: (page: number) => void;
};

type ServerSort = {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange: (column: string) => void;
};

interface ParticipantsTableProps {
  participants: ParticipantsTableRow[];
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  totalActiveFilters?: number;
  onOpenFilters?: () => void;
  onClearFilters?: () => void;
  filterStateKey?: string;
  searchByStatus?: string;
  projectId?: string;
  demographicQuestions?: DemographicQuestion[];
  /** Called when the user removes a custom (additional_data) column from the table. */
  onDeleteCustomField?: (key: string) => void;
  onExport?: (ctx: ParticipantsExportContext) => void;
  onParticipantAction?: (
    action: string,
    participant: ParticipantsTableRow
  ) => void;
  toolbarRightContent?: JSX.Element | null;
  isExportDisabled?: boolean;
  loading?: boolean;
  /**
   * When provided, the table is in server-side pagination mode: `participants`
   * is already the current page (no client slicing), and paging/sorting are
   * delegated to the parent. Omit for the legacy client-side behaviour.
   */
  serverPagination?: ServerPagination;
  serverSort?: ServerSort;
}

type CustomColumn = {id: string; name: string; type: string; required: boolean};
type ColumnVisibility = {
  phone: boolean;
  gender: boolean;
  dob: boolean;
  projectStatus: boolean;
  endorsedDate: boolean;
  participantType: boolean;
  primaryParticipantId: boolean;
  relationStatus: boolean;
  familyCode: boolean;
  resourceType: boolean;
  agencyName: boolean;
};
type EditingCell = {participantId: string | number; columnId: string} | null;
type CustomColumnData = Record<string, Record<string, string>>;

// --- Sort value resolution (extracted from the table's sort comparator) ---

type SortValueResult = {value: string | number; isEmpty: boolean; sortable: boolean};
const UNSORTABLE: SortValueResult = {value: '', isEmpty: true, sortable: false};

const lowerString = (v: unknown): string =>
  (typeof v === 'string' || typeof v === 'number' ? String(v) : '').toLowerCase();

const booleanSortRank = (raw: string): number => {
  if (raw === 'true') return 1;
  if (raw === 'false') return 0;
  return -1;
};

const standardSortValue = (
  row: ParticipantsTableRow,
  sortColumn: string,
): SortValueResult | null => {
  switch (sortColumn) {
    case 'name': { const v = lowerString(row.name); return {value: v, isEmpty: v === '', sortable: true}; }
    case 'email': { const v = lowerString(row.email); return {value: v, isEmpty: v === '', sortable: true}; }
    case 'gender': { const v = lowerString(row.gender); return {value: v, isEmpty: v === '', sortable: true}; }
    case 'status': { const v = lowerString(row.schedule_status); return {value: v, isEmpty: v === '', sortable: true}; }
    // dob empties keep the legacy behaviour of sorting as epoch 0 (never pushed to the end).
    case 'dob': { const v = row.dob ? new Date(row.dob).getTime() : 0; return {value: v, isEmpty: false, sortable: true}; }
    case 'joinDate': { const v = row.joinDate ? new Date(row.joinDate).getTime() : 0; return {value: v, isEmpty: v === 0 || Number.isNaN(v), sortable: true}; }
    default: return null;
  }
};

const customSortValue = (
  row: ParticipantsTableRow,
  columnId: string,
  type: string | undefined,
  customColumnData: Record<string, Record<string, string>>,
): SortValueResult => {
  const raw = customColumnData[String(row.id)]?.[columnId] || '';
  if (type === 'number') {
    const v = raw ? Number.parseFloat(raw) || 0 : 0;
    return {value: v, isEmpty: v === 0 || Number.isNaN(v), sortable: true};
  }
  if (type === 'date') {
    const v = raw ? new Date(raw).getTime() : 0;
    return {value: v, isEmpty: v === 0 || Number.isNaN(v), sortable: true};
  }
  if (type === 'boolean') {
    return {value: booleanSortRank(raw), isEmpty: !raw, sortable: true};
  }
  return {value: (raw || '').toLowerCase(), isEmpty: !raw, sortable: true};
};

export const demographicSortValue = (row: ParticipantsTableRow, answerKey: string): SortValueResult => {
  const answer = (row as unknown as Record<string, unknown>)[answerKey];
  let value = '';
  if (Array.isArray(answer)) {
    value = answer.join(', ').toLowerCase();
  } else if (answer && (typeof answer === 'string' || typeof answer === 'number' || typeof answer === 'boolean')) {
    // Only primitives are stringified; objects (and null/undefined) fall through to '' and sort as empty.
    value = String(answer).toLowerCase();
  }
  return {value, isEmpty: value === '', sortable: true};
};

const getParticipantSortValue = (
  row: ParticipantsTableRow,
  sortColumn: string,
  customColumns: Array<{id: string; type: string}>,
  customColumnData: Record<string, Record<string, string>>,
  demographicQuestions: DemographicQuestion[],
): SortValueResult => {
  if (sortColumn.startsWith('custom_')) {
    const columnId = sortColumn.slice('custom_'.length);
    const column = customColumns.find((c) => c.id === columnId);
    return customSortValue(row, columnId, column?.type, customColumnData);
  }
  if (sortColumn.startsWith('demographic_')) {
    const questionId = sortColumn.slice('demographic_'.length);
    const question = demographicQuestions.find((q) => String(q.question_id) === questionId);
    return question ? demographicSortValue(row, question.key) : UNSORTABLE;
  }
  return standardSortValue(row, sortColumn) ?? UNSORTABLE;
};

// Empty values sort to the end; otherwise compare by the resolved value + direction.
const compareParticipants = (
  a: ParticipantsTableRow,
  b: ParticipantsTableRow,
  sortColumn: string,
  sortDirection: 'asc' | 'desc',
  customColumns: Array<{id: string; type: string}>,
  customColumnData: Record<string, Record<string, string>>,
  demographicQuestions: DemographicQuestion[],
): number => {
  const av = getParticipantSortValue(a, sortColumn, customColumns, customColumnData, demographicQuestions);
  const bv = getParticipantSortValue(b, sortColumn, customColumns, customColumnData, demographicQuestions);
  if (!av.sortable || !bv.sortable) return 0;
  if (av.isEmpty) return 1;
  if (bv.isEmpty) return -1;
  if (av.value < bv.value) return sortDirection === 'asc' ? -1 : 1;
  if (av.value > bv.value) return sortDirection === 'asc' ? 1 : -1;
  return 0;
};

const getCustomColumnInputType = (columnType: string): string => {
  if (columnType === 'number') return 'number';
  if (columnType === 'date') return 'date';
  return 'text';
};

// --- Pure helpers extracted from the component to keep its cognitive load low ---

const isMissingValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

const formatCustomColumnDisplayName = (name: string): string =>
  (name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const getCustomColumnDisplay = (
  columnType: string | undefined,
  rawValue: string,
): string => {
  if (columnType === 'boolean') {
    if (rawValue === 'true') return 'True';
    if (rawValue === 'false') return 'False';
    return '';
  }
  return rawValue;
};

const getInitialEditValue = (
  columnType: string | undefined,
  currentValue: string,
): string => {
  if (columnType === 'boolean') {
    return currentValue === 'true' || currentValue === 'false' ? currentValue : '';
  }
  return currentValue;
};

// Returns false when the edited value is invalid and must not be persisted.
const isValidCustomEditValue = (
  columnType: string | undefined,
  value: string,
): boolean => {
  if (columnType === 'boolean') {
    return value === 'true' || value === 'false' || value === '';
  }
  if (columnType === 'number') {
    return !(value && isNaN(Number(value)));
  }
  return true;
};

const safeParseJson = <T,>(raw: string | null, label: string): T | undefined => {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch (e) {
    console.error(`Failed to parse ${label}:`, e);
    return undefined;
  }
};

const buildFieldByOneformaId = (
  participants: ParticipantsTableRow[],
  field: 'name' | 'email',
): Map<string, string> => {
  const byOneformaId = new Map<string, string>();
  participants.forEach((p) => {
    if (p.oneforma_user_id === null || p.oneforma_user_id === undefined) return;
    const id = String(p.oneforma_user_id).trim();
    if (!id) return;
    const value = (p[field] || '').trim();
    if (value) {
      byOneformaId.set(id, value);
    }
  });
  return byOneformaId;
};

const buildEarliestSecondaryByPrimary = (
  participants: ParticipantsTableRow[],
): Map<string, string> => {
  const byPrimaryId = new Map<string, ParticipantsTableRow[]>();
  for (const p of participants) {
    if (p.oneforma_primary_user_id === null || p.oneforma_primary_user_id === undefined) {
      continue;
    }
    const primaryId = String(p.oneforma_primary_user_id).trim();
    if (!primaryId) continue;
    const list = byPrimaryId.get(primaryId) ?? [];
    list.push(p);
    byPrimaryId.set(primaryId, list);
  }

  const resolved = new Map<string, string>();
  for (const [primaryId, linked] of byPrimaryId.entries()) {
    const hasTwinRelation = linked.some(
      (m) =>
        normalizeRelationshipLabelForUi(
          String(m.family_relationship_type || m.relationship || m.role_in_family || ''),
        ).toLowerCase() === 'twin',
    );
    // Twin relation is handled by existing backend link semantics.
    if (hasTwinRelation) continue;

    const sorted = [...linked].sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : Number.MAX_SAFE_INTEGER;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : Number.MAX_SAFE_INTEGER;
      if (aTime !== bTime) return aTime - bTime;
      return String(a.id).localeCompare(String(b.id));
    });

    const firstSecondary = sorted.find(
      (m) =>
        m.oneforma_user_id !== null &&
        m.oneforma_user_id !== undefined &&
        String(m.oneforma_user_id).trim(),
    );
    if (!firstSecondary) continue;
    resolved.set(primaryId, String(firstSecondary.oneforma_user_id).trim());
  }
  return resolved;
};

const sortParticipants = (
  participants: ParticipantsTableRow[],
  sortColumn: string | null,
  sortDirection: 'asc' | 'desc',
  customColumns: CustomColumn[],
  customColumnData: CustomColumnData,
  demographicQuestions: DemographicQuestion[],
): ParticipantsTableRow[] => {
  if (!sortColumn) return participants;
  return [...participants].sort((a, b) =>
    compareParticipants(
      a,
      b,
      sortColumn,
      sortDirection,
      customColumns,
      customColumnData,
      demographicQuestions,
    ),
  );
};

const computeColSpan = (
  columnVisibility: ColumnVisibility,
  customColumns: CustomColumn[],
  customColumnVisibility: Record<string, boolean>,
  demographicQuestions: DemographicQuestion[],
  isDemographicColumnVisible: (key: string) => boolean,
  showActionColumn: boolean,
): number => {
  const optionalColumns = [
    columnVisibility.phone,
    columnVisibility.gender,
    columnVisibility.dob,
    columnVisibility.projectStatus,
    columnVisibility.endorsedDate,
    columnVisibility.participantType,
    columnVisibility.primaryParticipantId,
    columnVisibility.relationStatus,
    columnVisibility.familyCode,
    columnVisibility.resourceType,
    columnVisibility.agencyName,
  ].filter(Boolean).length;
  const customVisible = customColumns.filter((c) => customColumnVisibility[c.id]).length;
  const demographicVisible = demographicQuestions.filter((q) =>
    isDemographicColumnVisible(q.key),
  ).length;
  // Name column + visible optional/custom/demographic columns + Updated At + Action.
  return 1 + optionalColumns + customVisible + demographicVisible + 1 + (showActionColumn ? 1 : 0);
};

type PageToken = number | 'ellipsis-start' | 'ellipsis-end';

const computeVisiblePages = (
  effectivePage: number,
  totalPages: number,
): PageToken[] => {
  const VISIBLE = 5;
  const pages: PageToken[] = [];
  let start: number;
  let end: number;
  if (totalPages <= VISIBLE) {
    start = 1;
    end = totalPages;
  } else if (effectivePage <= Math.ceil(VISIBLE / 2) + 1) {
    start = 1;
    end = VISIBLE;
  } else if (effectivePage >= totalPages - Math.floor(VISIBLE / 2) - 1) {
    start = totalPages - VISIBLE + 1;
    end = totalPages;
  } else {
    start = effectivePage - Math.floor(VISIBLE / 2);
    end = effectivePage + Math.floor(VISIBLE / 2);
  }
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push('ellipsis-start');
  }
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push('ellipsis-end');
    pages.push(totalPages);
  }
  return pages;
};

interface TableView {
  effectivePage: number;
  totalCount: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  currentParticipants: ParticipantsTableRow[];
  activeSortColumn: string | null;
  activeSortDirection: 'asc' | 'desc';
}

// Server mode: `participants` is already the current page; paging/total come
// from the server. Client mode: derive everything from the full in-memory set.
const getTableView = (params: {
  isServerMode: boolean;
  serverPagination?: ServerPagination;
  serverSort?: ServerSort;
  currentPage: number;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  itemsPerPage: number;
  sortedParticipants: ParticipantsTableRow[];
  participants: ParticipantsTableRow[];
}): TableView => {
  const {
    isServerMode,
    serverPagination,
    serverSort,
    currentPage,
    sortColumn,
    sortDirection,
    itemsPerPage,
    sortedParticipants,
    participants,
  } = params;
  const effectivePage = isServerMode ? serverPagination!.page : currentPage;
  const totalCount = isServerMode ? serverPagination!.total : sortedParticipants.length;
  const totalPages = isServerMode
    ? Math.max(1, serverPagination!.pages)
    : Math.ceil(sortedParticipants.length / itemsPerPage);
  const startIndex = (effectivePage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentParticipants = isServerMode
    ? participants
    : sortedParticipants.slice(startIndex, endIndex);
  // Header sort indicators reflect the parent-owned sort in server mode.
  const activeSortColumn = isServerMode ? serverSort?.sortBy ?? null : sortColumn;
  const activeSortDirection = isServerMode ? serverSort?.sortDir ?? 'asc' : sortDirection;
  return {
    effectivePage,
    totalCount,
    totalPages,
    startIndex,
    endIndex,
    currentParticipants,
    activeSortColumn,
    activeSortDirection,
  };
};

const changePage = (
  page: number,
  ctx: {
    isServerMode: boolean;
    serverPagination?: ServerPagination;
    setCurrentPage: (page: number) => void;
  },
) => {
  if (ctx.isServerMode) {
    ctx.serverPagination!.onPageChange(page);
    return;
  }
  ctx.setCurrentPage(page);
};

const applySortChange = (
  column: string,
  ctx: {
    isServerMode: boolean;
    serverSort?: ServerSort;
    sortColumn: string | null;
    sortDirection: 'asc' | 'desc';
    setSortColumn: (column: string) => void;
    setSortDirection: (direction: 'asc' | 'desc') => void;
    setCurrentPage: (page: number) => void;
  },
) => {
  if (ctx.isServerMode) {
    // Server decides ordering; parent maps the column + toggles direction.
    ctx.serverSort?.onSortChange(column);
    return;
  }
  if (ctx.sortColumn === column) {
    ctx.setSortDirection(ctx.sortDirection === 'asc' ? 'desc' : 'asc');
  } else {
    ctx.setSortColumn(column);
    ctx.setSortDirection('asc');
  }
  // Reset to first page when sorting changes.
  ctx.setCurrentPage(1);
};

const dispatchParticipantAction = (
  action: string,
  participant: ParticipantsTableRow,
  ctx: {
    setSelectedParticipant: (p: ParticipantsTableRow | null) => void;
    setIsDetailsDialogOpen: (open: boolean) => void;
    setDetailsInitialTab: (tab: 'details' | 'history') => void;
    setIsStatusDialogOpen: (open: boolean) => void;
    onParticipantAction?: (action: string, participant: ParticipantsTableRow) => void;
  },
) => {
  if (action === 'view') {
    ctx.setSelectedParticipant(participant);
    ctx.setDetailsInitialTab('details');
    ctx.setIsDetailsDialogOpen(true);
  } else if (action === 'history') {
    ctx.setSelectedParticipant(participant);
    ctx.setDetailsInitialTab('history');
    ctx.setIsDetailsDialogOpen(true);
  } else if (action === 'change-status') {
    ctx.setSelectedParticipant(participant);
    ctx.setIsStatusDialogOpen(true);
  } else if (ctx.onParticipantAction) {
    ctx.onParticipantAction(action, participant);
  }
};

const saveCustomEdit = (params: {
  editingCell: EditingCell;
  editingValue: string;
  customColumns: CustomColumn[];
  customColumnData: CustomColumnData;
  storageKey: string;
  setCustomColumnData: (data: CustomColumnData) => void;
  onDone: () => void;
}) => {
  const {editingCell, editingValue, customColumns, customColumnData, storageKey, setCustomColumnData, onDone} = params;
  if (!editingCell) return;
  const {participantId, columnId} = editingCell;
  const column = customColumns.find((col) => col.id === columnId);
  if (!isValidCustomEditValue(column?.type, editingValue)) {
    onDone();
    return;
  }
  const updatedData = {
    ...customColumnData,
    [participantId]: {
      ...customColumnData[participantId],
      [columnId]: editingValue,
    },
  };
  setCustomColumnData(updatedData);
  localStorage.setItem(storageKey, JSON.stringify(updatedData));
  onDone();
};

// Load persisted custom column definitions/data from localStorage into state.
// Column VISIBILITY is not loaded here — it persists per-user server-side
// (see useColumnPreferences above).
const applyPersistedTableState = (
  getKey: (key: string) => string,
  setters: {
    setCustomColumns: (columns: CustomColumn[]) => void;
    setCustomColumnData: (data: CustomColumnData) => void;
  },
) => {
  const columns = safeParseJson<CustomColumn[]>(
    localStorage.getItem(getKey('custom_columns')),
    'saved custom columns',
  );
  if (columns) setters.setCustomColumns(columns);

  const data = safeParseJson<CustomColumnData>(
    localStorage.getItem(getKey('custom_data')),
    'saved custom data',
  );
  if (data) setters.setCustomColumnData(data);
};

interface CustomColumnEditorProps {
  readonly columnType: string;
  readonly editingValue: string;
  readonly setEditingValue: (value: string) => void;
  readonly saveEdit: () => void;
  readonly cancelEdit: () => void;
}

function CustomColumnEditor({
  columnType,
  editingValue,
  setEditingValue,
  saveEdit,
  cancelEdit,
}: CustomColumnEditorProps): JSX.Element {
  if (columnType === 'boolean') {
    return (
      <Select
        value={editingValue}
        onValueChange={setEditingValue}
        onOpenChange={(open) => {
          if (!open) saveEdit();
        }}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue placeholder="Select value" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">True</SelectItem>
          <SelectItem value="false">False</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      value={editingValue}
      onChange={(e) => setEditingValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') saveEdit();
        if (e.key === 'Escape') cancelEdit();
      }}
      onBlur={saveEdit}
      autoFocus
      className="h-8 text-sm"
      type={getCustomColumnInputType(columnType)}
    />
  );
}

// --- Toolbar actions (filters, columns selector, export) --------------------

interface ParticipantsTableActionsProps {
  totalActiveFilters: number;
  onOpenFilters?: () => void;
  onClearFilters?: () => void;
  toolbarRightContent: JSX.Element | null;
  columnVisibility: ColumnVisibility;
  onColumnVisibilityChange: (column: string, checked: boolean) => void;
  customColumns: CustomColumn[];
  customColumnVisibility: Record<string, boolean>;
  onToggleCustomColumn: (columnId: string) => void;
  demographicQuestions: DemographicQuestion[];
  isDemographicColumnVisible: (key: string) => boolean;
  onToggleDemographicColumn: (key: string) => void;
  onDeleteCustomField?: (key: string) => void;
  onExport?: (ctx: ParticipantsExportContext) => void;
  customColumnData: CustomColumnData;
  isExportDisabled: boolean;
}

const STANDARD_COLUMN_OPTIONS: Array<{id: keyof ColumnVisibility; label: string}> = [
  {id: 'phone', label: 'Contact Number'},
  {id: 'gender', label: 'Gender'},
  {id: 'dob', label: 'DOB'},
  {id: 'projectStatus', label: 'Participant Status'},
  {id: 'endorsedDate', label: 'Endorsed Date'},
  {id: 'participantType', label: 'Participant Type'},
  {id: 'primaryParticipantId', label: 'Related Participant Name'},
  {id: 'relationStatus', label: 'Relationship'},
  {id: 'familyCode', label: 'Family Code'},
  {id: 'resourceType', label: 'Resource Type'},
  {id: 'agencyName', label: 'Agency Name'},
];

function ParticipantsTableActions({
  totalActiveFilters,
  onOpenFilters,
  onClearFilters,
  toolbarRightContent,
  columnVisibility,
  onColumnVisibilityChange,
  customColumns,
  customColumnVisibility,
  onToggleCustomColumn,
  demographicQuestions,
  isDemographicColumnVisible,
  onToggleDemographicColumn,
  onDeleteCustomField,
  onExport,
  customColumnData,
  isExportDisabled,
}: Readonly<ParticipantsTableActionsProps>): JSX.Element {
  const standardOptions = STANDARD_COLUMN_OPTIONS.map(({id, label}) => ({
    id,
    label,
    checked: Boolean(columnVisibility[id]),
    onCheckedChange: (checked: boolean) => onColumnVisibilityChange(id, checked),
  }));

  const customOptions = customColumns.map((column) => ({
    id: column.id,
    label: formatCustomColumnDisplayName(column.name),
    checked: customColumnVisibility[column.id] ?? false,
    onCheckedChange: () => onToggleCustomColumn(column.id),
  }));

  const demographicOptions = demographicQuestions.map((question) => {
    // Synthetic questions (additional_data-derived custom fields) can be
    // permanently removed; real questions cannot.
    const canDelete = !!question.isSynthetic && !!onDeleteCustomField;
    return {
      id: `demographic_${question.question_id}`,
      label: question.question,
      checked: isDemographicColumnVisible(question.key),
      onCheckedChange: () => onToggleDemographicColumn(question.key),
      ...(canDelete ? {onDelete: () => onDeleteCustomField!(question.key)} : {}),
    };
  });

  return (
    <>
      {totalActiveFilters > 0 && onClearFilters ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="text-destructive hover:text-destructive text-xs"
        >
          Clear Filters ({totalActiveFilters})
        </Button>
      ) : null}
      {onOpenFilters ? (
        <Button variant="outline" className="gap-2" onClick={onOpenFilters}>
          <Filter className="h-4 w-4" />
          Filter
          {totalActiveFilters > 0 ? (
            <span className="ml-1 text-xs opacity-70">({totalActiveFilters})</span>
          ) : null}
          <ChevronDown className="h-4 w-4" />
        </Button>
      ) : null}
      {toolbarRightContent}
      <ColumnsSelectorPopover
        options={[...standardOptions, ...customOptions, ...demographicOptions]}
      />
      <ExportButton
        onClick={() =>
          onExport?.({
            columnVisibility,
            customColumns,
            customColumnData,
            customColumnVisibility,
            demographicQuestions: demographicQuestions.map((q) => ({
              question_id: q.question_id,
              question: q.question,
              key: q.key,
            })),
          })
        }
        disabled={isExportDisabled}
      />
    </>
  );
}

// --- Table header -----------------------------------------------------------

interface ParticipantsTableHeadProps {
  columnVisibility: ColumnVisibility;
  customColumns: CustomColumn[];
  customColumnVisibility: Record<string, boolean>;
  demographicQuestions: DemographicQuestion[];
  isDemographicColumnVisible: (key: string) => boolean;
  activeSortColumn: string | null;
  activeSortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  showActionColumn: boolean;
}

const STANDARD_HEADER_COLUMNS: Array<{
  key: keyof ColumnVisibility;
  label: string;
  columnId: string;
  sortable: boolean;
}> = [
  {key: 'phone', label: 'Contact Number', columnId: 'phone', sortable: false},
  {key: 'gender', label: 'Gender', columnId: 'gender', sortable: true},
  {key: 'dob', label: 'DOB', columnId: 'dob', sortable: true},
  {key: 'projectStatus', label: 'Participant Status', columnId: 'status', sortable: true},
  {key: 'endorsedDate', label: 'Endorsed Date', columnId: 'endorsed_date', sortable: true},
  {key: 'participantType', label: 'Participant Type', columnId: 'participant_type', sortable: true},
  {key: 'primaryParticipantId', label: 'Related Participant Name', columnId: 'related_participant_name', sortable: true},
  {key: 'relationStatus', label: 'Relationship', columnId: 'relationship', sortable: true},
  {key: 'familyCode', label: 'Family Code', columnId: 'family_code', sortable: true},
  {key: 'resourceType', label: 'Resource Type', columnId: 'resource_type', sortable: true},
  {key: 'agencyName', label: 'Agency Name', columnId: 'agency_name', sortable: true},
];

function DemographicHeaderCell({
  question,
  onSort,
  sortColumn,
  sortDirection,
}: Readonly<{
  question: DemographicQuestion;
  onSort: (column: string) => void;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
}>): JSX.Element {
  const columnId = `demographic_${question.question_id}`;
  return (
    <TableHead className="whitespace-nowrap">
      <button
        type="button"
        onClick={() => onSort(columnId)}
        className="flex items-center justify-between gap-2 hover:text-slate-900 transition-colors w-full text-left"
      >
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span className="truncate" title={question.question}>
            {question.question.length > 30
              ? `${question.question.substring(0, 30)}...`
              : question.question}
          </span>
          {question.question_data && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[300px] border border-border bg-popover text-popover-foreground text-xs">
                  <div className="text-xs">
                    <p className="font-semibold mb-1 text-xs leading-tight">{question.question}</p>
                    {question.question_data.questionItems && (
                      <p className="text-xs leading-tight text-muted-foreground">
                        Options: {JSON.stringify(question.question_data.questionItems)}
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex flex-col flex-shrink-0 ml-1">
          <ChevronUp
            className={cn(
              'h-3 w-3',
              sortColumn === columnId && sortDirection === 'asc'
                ? 'text-primary'
                : 'text-muted-foreground opacity-40',
            )}
          />
          <ChevronDown
            className={cn(
              'h-3 w-3 -mt-1',
              sortColumn === columnId && sortDirection === 'desc'
                ? 'text-primary'
                : 'text-muted-foreground opacity-40',
            )}
          />
        </div>
      </button>
    </TableHead>
  );
}

function ParticipantsTableHead({
  columnVisibility,
  customColumns,
  customColumnVisibility,
  demographicQuestions,
  isDemographicColumnVisible,
  activeSortColumn,
  activeSortDirection,
  onSort,
  sortColumn,
  sortDirection,
  showActionColumn,
}: Readonly<ParticipantsTableHeadProps>): JSX.Element {
  return (
    <TableRow>
      <TableHead className="whitespace-nowrap min-w-[220px] sticky left-0 z-30 bg-slate-50 bg-clip-padding">
        <SortableHeaderCell
          label="Name"
          columnId="name"
          activeColumn={activeSortColumn}
          direction={activeSortDirection}
          onSort={onSort}
        />
      </TableHead>
      {STANDARD_HEADER_COLUMNS.filter((col) => columnVisibility[col.key]).map((col) => (
        <TableHead key={col.key} className="whitespace-nowrap">
          {col.sortable ? (
            <SortableHeaderCell
              label={col.label}
              columnId={col.columnId}
              activeColumn={activeSortColumn}
              direction={activeSortDirection}
              onSort={onSort}
            />
          ) : (
            col.label
          )}
        </TableHead>
      ))}
      {customColumns
        .filter((column) => customColumnVisibility[column.id])
        .map((column) => (
          <TableHead key={column.id} className="whitespace-nowrap">
            <SortableHeaderCell
              label={formatCustomColumnDisplayName(column.name)}
              columnId={`custom_${column.id}`}
              activeColumn={activeSortColumn}
              direction={activeSortDirection}
              onSort={onSort}
            />
          </TableHead>
        ))}
      {/* Demographic Question Columns */}
      {demographicQuestions
        .filter((question) => isDemographicColumnVisible(question.key))
        .map((question) => (
          <DemographicHeaderCell
            key={question.question_id}
            question={question}
            onSort={onSort}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
          />
        ))}
      <TableHead className="whitespace-nowrap">Updated At</TableHead>
      {showActionColumn && (
        <TableHead className="whitespace-nowrap text-center">
          <span className="text-sm font-medium text-slate-700">Action</span>
        </TableHead>
      )}
    </TableRow>
  );
}

// --- Row cells --------------------------------------------------------------

function RelatedParticipantCell({
  participant,
  nameByOneformaId,
  emailByOneformaId,
  earliestSecondaryByPrimaryId,
}: Readonly<{
  participant: ParticipantsTableRow;
  nameByOneformaId: Map<string, string>;
  emailByOneformaId: Map<string, string>;
  earliestSecondaryByPrimaryId: Map<string, string>;
}>): JSX.Element {
  if (isSoloPrimaryNoFamilyMembers(participant)) {
    return (
      <span className="text-muted-foreground text-xs font-sans">
        {FAMILY_GRID_NOT_APPLICABLE}
      </span>
    );
  }
  // Prefer the server-resolved name when present (the list resolves it over the
  // full project set while sorting by this column) so display matches sort order.
  const serverRelatedName = (participant as any).related_participant_name;
  if (serverRelatedName != null && String(serverRelatedName).trim() !== '') {
    return <span>{String(serverRelatedName)}</span>;
  }
  const participantOneformaId =
    participant.oneforma_user_id === null || participant.oneforma_user_id === undefined
      ? ''
      : String(participant.oneforma_user_id).trim();
  const isPrimary = isMissingValue(participant.oneforma_primary_user_id);
  const earliestSecondaryForPrimary =
    isPrimary && participantOneformaId
      ? earliestSecondaryByPrimaryId.get(participantOneformaId) || ''
      : '';
  const relatedId = String(
    earliestSecondaryForPrimary ||
      participant.related_participant_ref ||
      participant.oneforma_primary_user_id ||
      '',
  ).trim();
  if (!relatedId) return <EmptyValue />;
  const relatedName = nameByOneformaId.get(relatedId);
  if (!relatedName) return <EmptyValue />;
  const relatedEmail = emailByOneformaId.get(relatedId);
  return <span title={relatedEmail || undefined}>{relatedName}</span>;
}

function RelationshipCell({participant}: Readonly<{participant: ParticipantsTableRow}>): JSX.Element {
  if (isSoloPrimaryNoFamilyMembers(participant)) {
    return (
      <span className="text-muted-foreground text-xs font-sans">
        {FAMILY_GRID_NOT_APPLICABLE}
      </span>
    );
  }
  const raw =
    participant.family_relationship_type ||
    participant.relationship ||
    participant.role_in_family;
  if (isMissingValue(raw)) return <EmptyValue />;
  return <>{normalizeRelationshipLabelForUi(String(raw))}</>;
}

function FamilyCodeCell({participant}: Readonly<{participant: ParticipantsTableRow}>): JSX.Element {
  if (isSoloPrimaryNoFamilyMembers(participant)) {
    return (
      <span className="text-muted-foreground text-xs font-sans">
        {FAMILY_GRID_NOT_APPLICABLE}
      </span>
    );
  }
  if (isMissingValue(participant.family_group_code)) return <EmptyValue />;
  return <span className="font-mono">{String(participant.family_group_code)}</span>;
}

/** Explicit string conversion for demographic answers of unknown shape — avoids the
 * default "[object Object]" coercion for plain objects while leaving primitives untouched. */
function stringifyDemographicAnswer(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
}

function DemographicAnswerCell({answer}: Readonly<{answer: unknown}>): JSX.Element {
  if (!answer) return <EmptyValue />;
  if (Array.isArray(answer)) return <>{answer.join(', ')}</>;
  return <>{stringifyDemographicAnswer(answer)}</>;
}

interface ParticipantRowProps {
  participant: ParticipantsTableRow;
  columnVisibility: ColumnVisibility;
  customColumns: CustomColumn[];
  customColumnVisibility: Record<string, boolean>;
  demographicQuestions: DemographicQuestion[];
  isDemographicColumnVisible: (key: string) => boolean;
  editingCell: EditingCell;
  editingValue: string;
  setEditingValue: (value: string) => void;
  saveEdit: () => void;
  cancelEdit: () => void;
  startEditing: (participantId: string | number, columnId: string) => void;
  getCustomColumnValue: (participantId: string | number, columnId: string) => string;
  onAction: (action: string, participant: ParticipantsTableRow) => void;
  showActionColumn: boolean;
  nameByOneformaId: Map<string, string>;
  emailByOneformaId: Map<string, string>;
  earliestSecondaryByPrimaryId: Map<string, string>;
}

const DATA_CELL_CLASS = cn('py-3 whitespace-nowrap', PARTICIPANT_ROSTER_DATA_TEXT_CLASS);

function ParticipantRow({
  participant,
  columnVisibility,
  customColumns,
  customColumnVisibility,
  demographicQuestions,
  isDemographicColumnVisible,
  editingCell,
  editingValue,
  setEditingValue,
  saveEdit,
  cancelEdit,
  startEditing,
  getCustomColumnValue,
  onAction,
  showActionColumn,
  nameByOneformaId,
  emailByOneformaId,
  earliestSecondaryByPrimaryId,
}: Readonly<ParticipantRowProps>): JSX.Element {
  return (
    <TableRow className="group hover:bg-muted/20 transition-colors">
      <TableCell className="py-3 min-w-[220px] sticky left-0 z-30 bg-white bg-clip-padding group-hover:bg-white">
        <div className="flex items-center gap-3">
          <Avatar className="size-8">
            <AvatarFallback>
              {participant.name
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 overflow-hidden">
            <div className="font-medium truncate" title={participant.name}>
              {participant.name}
            </div>
            <div className="text-sm text-muted-foreground truncate" title={participant.email}>
              {participant.email}
            </div>
          </div>
        </div>
      </TableCell>
      {columnVisibility.phone && (
        <TableCell className={DATA_CELL_CLASS}>
          {isMissingValue(participant.phone) ? <EmptyValue /> : participant.phone}
        </TableCell>
      )}
      {columnVisibility.gender && (
        <TableCell className={DATA_CELL_CLASS}>
          {isMissingValue(participant.gender) ? <EmptyValue /> : participant.gender}
        </TableCell>
      )}
      {columnVisibility.dob && (
        <TableCell className={DATA_CELL_CLASS}>
          {isMissingValue(participant.dob) ? 'N/A' : participant.dob}
        </TableCell>
      )}
      {columnVisibility.projectStatus && (
        <TableCell className="py-3 whitespace-nowrap">
          <Badge
            variant="outline"
            className={getStatusColor(participant.schedule_status || 'endorsed')}
          >
            {formatStatusDisplay(participant.schedule_status || 'endorsed')}
          </Badge>
        </TableCell>
      )}
      {columnVisibility.endorsedDate && (
        <TableCell className={DATA_CELL_CLASS}>
          {isMissingValue(participant.endorsed_date) ? (
            <EmptyValue />
          ) : (
            String(participant.endorsed_date)
          )}
        </TableCell>
      )}
      {columnVisibility.participantType && (
        <TableCell className="py-3 whitespace-nowrap">
          {getParticipantTypeForDisplay(participant)}
        </TableCell>
      )}
      {columnVisibility.primaryParticipantId && (
        <TableCell className="py-3 whitespace-nowrap text-xs">
          <RelatedParticipantCell
            participant={participant}
            nameByOneformaId={nameByOneformaId}
            emailByOneformaId={emailByOneformaId}
            earliestSecondaryByPrimaryId={earliestSecondaryByPrimaryId}
          />
        </TableCell>
      )}
      {columnVisibility.relationStatus && (
        <TableCell className="py-3 whitespace-nowrap text-xs">
          <RelationshipCell participant={participant} />
        </TableCell>
      )}
      {columnVisibility.familyCode && (
        <TableCell className="py-3 whitespace-nowrap text-xs">
          <FamilyCodeCell participant={participant} />
        </TableCell>
      )}
      {columnVisibility.resourceType && (
        <TableCell className="py-3 whitespace-nowrap">
          {isMissingValue(participant.resource_type) ? '—' : String(participant.resource_type)}
        </TableCell>
      )}
      {columnVisibility.agencyName && (
        <TableCell className="py-3 whitespace-nowrap">
          {isMissingValue(participant.agency_name) ? '—' : String(participant.agency_name)}
        </TableCell>
      )}
      {customColumns
        .filter((column) => customColumnVisibility[column.id])
        .map((column) => {
          const isEditing =
            editingCell?.participantId === participant.id &&
            editingCell?.columnId === column.id;
          return (
            <TableCell key={column.id} className="py-3 whitespace-nowrap">
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <CustomColumnEditor
                    columnType={column.type}
                    editingValue={editingValue}
                    setEditingValue={setEditingValue}
                    saveEdit={saveEdit}
                    cancelEdit={cancelEdit}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className="cursor-pointer hover:bg-gray-50 p-1 rounded min-h-[32px] flex items-center text-left w-full"
                  onClick={() => startEditing(participant.id, column.id)}
                >
                  {getCustomColumnValue(participant.id, column.id) || <EmptyValue />}
                </button>
              )}
            </TableCell>
          );
        })}
      {/* Demographic Answer Cells */}
      {demographicQuestions
        .filter((question) => isDemographicColumnVisible(question.key))
        .map((question) => (
          <TableCell key={question.question_id} className={DATA_CELL_CLASS}>
            <DemographicAnswerCell answer={(participant as any)[question.key]} />
          </TableCell>
        ))}
      <TableCell className={DATA_CELL_CLASS}>
        {participant.demographics_updated_on ?? '-'}
      </TableCell>
      {showActionColumn && (
        <TableCell className="py-3 text-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 rounded-md border border-transparent hover:border-slate-200 hover:bg-slate-100"
              >
                <MoreVertical className="h-4 w-4 text-slate-700" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="gap-2" onClick={() => onAction('view', participant)}>
                <Info className="h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onClick={() => onAction('history', participant)}>
                <History className="h-4 w-4" />
                View History
              </DropdownMenuItem>
              <DropdownMenuItem
                className="gap-2 text-destructive focus:text-destructive"
                onClick={() => onAction('remove', participant)}
              >
                <Trash2 className="h-4 w-4" />
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      )}
    </TableRow>
  );
}

// --- Pagination -------------------------------------------------------------

function ParticipantsTablePagination({
  effectivePage,
  totalPages,
  onPageChange,
}: Readonly<{
  effectivePage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}>): JSX.Element {
  const pages = computeVisiblePages(effectivePage, totalPages);
  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (effectivePage > 1) {
                onPageChange(effectivePage - 1);
              }
            }}
            className={
              effectivePage <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'
            }
          />
        </PaginationItem>
        {pages.map((page) =>
          page === 'ellipsis-start' || page === 'ellipsis-end' ? (
            <PaginationItem key={page}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={page}>
              <PaginationLink
                href="#"
                isActive={page === effectivePage}
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(page);
                }}
                className="cursor-pointer"
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (effectivePage < totalPages) {
                onPageChange(effectivePage + 1);
              }
            }}
            className={
              effectivePage >= totalPages
                ? 'pointer-events-none opacity-50'
                : 'cursor-pointer'
            }
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

export default function ParticipantsTable({
  participants,
  searchTerm = '',
  onSearchChange,
  totalActiveFilters = 0,
  onOpenFilters,
  onClearFilters,
  filterStateKey = '',
  searchByStatus = '',
  projectId,
  demographicQuestions = [],
  onDeleteCustomField,
  onExport,
  onParticipantAction,
  toolbarRightContent = null,
  isExportDisabled = false,
  loading = false,
  serverPagination,
  serverSort,
}: Readonly<ParticipantsTableProps>) {
  const isServerMode = !!serverPagination;
  const {user} = useAuth();

  // Get project-specific role using RBAC hook
  const {role: projectRole, loading: roleLoading} = useProjectRole(projectId || undefined);

  const {isProjectManager, isModerator} = getProjectPermissionFlags({
    projectRole,
    roleLoading,
    userRole: user?.role,
    userEmail: user?.email,
  });
  const showActionColumn = isProjectManager && !isModerator;

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Column visibility persisted per-user, server-side (Participants screen). The
  // fixed columns follow the "new column hidden by default" rule (AC8); the
  // data-driven custom / demographic columns keep their default-visible
  // behaviour. Everything is stored in one map: fixed keys plus namespaced
  // `custom:<id>` / `demographic:<questionKey>` entries.
  const FIXED_COLUMN_DEFAULTS = useMemo(
    (): ColumnVisibility => ({
      phone: true,
      gender: true,
      dob: true,
      projectStatus: true,
      endorsedDate: true,
      participantType: true,
      primaryParticipantId: true,
      relationStatus: true,
      familyCode: true,
      resourceType: true,
      agencyName: true,
    }),
    [],
  );
  const {prefs: columnPrefs, save: saveColumnPrefs} =
    useColumnPreferences('participants');
  const columnVisibility = useMemo(
    () => applyColumnVisibilityPrefs(FIXED_COLUMN_DEFAULTS, columnPrefs),
    [FIXED_COLUMN_DEFAULTS, columnPrefs],
  );

  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customColumnData, setCustomColumnData] = useState<CustomColumnData>({});
  // Custom / demographic columns default to visible; a saved preference (keyed
  // `custom:<id>` / `demographic:<questionKey>`) overrides per column.
  const customColumnVisibility = useMemo(() => {
    const m: Record<string, boolean> = {};
    customColumns.forEach((c) => {
      m[c.id] = columnPrefs?.[`custom:${c.id}`] ?? true;
    });
    return m;
  }, [customColumns, columnPrefs]);
  const demographicColumnVisibility = useMemo(() => {
    const m: Record<string, boolean> = {};
    (demographicQuestions || []).forEach((q: any) => {
      m[q.key] = columnPrefs?.[`demographic:${q.key}`] ?? true;
    });
    return m;
  }, [demographicQuestions, columnPrefs]);
  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [editingValue, setEditingValue] = useState('');

  const [selectedParticipant, setSelectedParticipant] =
    useState<ParticipantsTableRow | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [detailsInitialTab, setDetailsInitialTab] = useState<'details' | 'history'>(
    'details',
  );
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Project-specific localStorage key namespace.
  const storageSuffix = projectId ? `_project_${projectId}` : '';
  const getProjectStorageKey = (key: string) => `participants_${key}${storageSuffix}`;

  // Participants arrive already filtered by the parent component.
  const sortedParticipants = useMemo(
    () =>
      sortParticipants(
        participants,
        sortColumn,
        sortDirection,
        customColumns,
        customColumnData,
        demographicQuestions,
      ),
    [participants, sortColumn, sortDirection, customColumns, customColumnData, demographicQuestions],
  );

  const {
    effectivePage,
    totalCount,
    totalPages,
    startIndex,
    endIndex,
    currentParticipants,
    activeSortColumn,
    activeSortDirection,
  } = getTableView({
    isServerMode,
    serverPagination,
    serverSort,
    currentPage,
    sortColumn,
    sortDirection,
    itemsPerPage,
    sortedParticipants,
    participants,
  });

  const participantNameByOneformaUserId = useMemo(
    () => buildFieldByOneformaId(participants, 'name'),
    [participants],
  );
  const participantEmailByOneformaUserId = useMemo(
    () => buildFieldByOneformaId(participants, 'email'),
    [participants],
  );
  const earliestSecondaryByPrimaryOneformaId = useMemo(
    () => buildEarliestSecondaryByPrimary(participants),
    [participants],
  );

  const isDemographicColumnVisible = (questionKey: string) =>
    demographicColumnVisibility[questionKey] !== false;

  const handlePageChange = (page: number) =>
    changePage(page, {isServerMode, serverPagination, setCurrentPage});

  const handleSort = (column: string) =>
    applySortChange(column, {
      isServerMode,
      serverSort,
      sortColumn,
      sortDirection,
      setSortColumn,
      setSortDirection,
      setCurrentPage,
    });

  const handleParticipantAction = (action: string, participant: ParticipantsTableRow) =>
    dispatchParticipantAction(action, participant, {
      setSelectedParticipant,
      setIsDetailsDialogOpen,
      setDetailsInitialTab,
      setIsStatusDialogOpen,
      onParticipantAction,
    });

  const handleColumnVisibilityChange = (column: string, checked: boolean) => {
    saveColumnPrefs({...buildColumnPrefMap(), [column]: checked});
  };

  const startEditing = (participantId: string | number, columnId: string) => {
    const currentValue = customColumnData[participantId]?.[columnId] || '';
    const column = customColumns.find((col) => col.id === columnId);
    setEditingCell({participantId, columnId});
    setEditingValue(getInitialEditValue(column?.type, currentValue));
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveEdit = () =>
    saveCustomEdit({
      editingCell,
      editingValue,
      customColumns,
      customColumnData,
      storageKey: getProjectStorageKey('custom_data'),
      setCustomColumnData,
      onDone: cancelEdit,
    });

  const getCustomColumnValue = (
    participantId: string | number,
    columnId: string,
  ): string => {
    const value = customColumnData[participantId]?.[columnId] || '';
    const column = customColumns.find((col) => col.id === columnId);
    return getCustomColumnDisplay(column?.type, value);
  };

  // Build the full per-screen visibility map from the currently-known columns,
  // so a save records every visible column (future-added columns stay absent →
  // hidden, per AC8).
  const buildColumnPrefMap = (): Record<string, boolean> => {
    const map: Record<string, boolean> = {};
    (Object.keys(FIXED_COLUMN_DEFAULTS) as string[]).forEach((k) => {
      map[k] = Boolean((columnVisibility as Record<string, boolean>)[k]);
    });
    customColumns.forEach((c) => {
      map[`custom:${c.id}`] = customColumnVisibility[c.id] ?? true;
    });
    (demographicQuestions || []).forEach((q: any) => {
      map[`demographic:${q.key}`] = isDemographicColumnVisible(q.key);
    });
    return map;
  };

  const toggleCustomColumnVisibility = (columnId: string) => {
    const current = customColumnVisibility[columnId] ?? true;
    saveColumnPrefs({...buildColumnPrefMap(), [`custom:${columnId}`]: !current});
  };

  const toggleDemographicColumnVisibility = (questionKey: string) => {
    const current = isDemographicColumnVisible(questionKey);
    saveColumnPrefs({
      ...buildColumnPrefMap(),
      [`demographic:${questionKey}`]: !current,
    });
  };


  // Load custom column definitions + data from localStorage on mount. Column
  // VISIBILITY is no longer read here — it now persists per-user server-side
  // (see useColumnPreferences above).
  useEffect(() => {
    applyPersistedTableState(getProjectStorageKey, {
      setCustomColumns,
      setCustomColumnData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, searchByStatus, filterStateKey]);

  const handleStatusChange = (participantId: string | number, newStatus: string) => {
    onParticipantAction?.('status-change', {
      id: participantId,
      status: newStatus,
    } as any);
  };

  const colSpan = computeColSpan(
    columnVisibility,
    customColumns,
    customColumnVisibility,
    demographicQuestions,
    isDemographicColumnVisible,
    showActionColumn,
  );

  const hasNoRows = loading || currentParticipants.length === 0;

  return (
    <div>
      <TableToolbar
        search={
          <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
            <TableSearchInput
              value={searchTerm}
              onChange={(value) => onSearchChange?.(value)}
              placeholder="Search participants..."
            />
          </div>
        }
        actions={
          <ParticipantsTableActions
            totalActiveFilters={totalActiveFilters}
            onOpenFilters={onOpenFilters}
            onClearFilters={onClearFilters}
            toolbarRightContent={toolbarRightContent}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            customColumns={customColumns}
            customColumnVisibility={customColumnVisibility}
            onToggleCustomColumn={toggleCustomColumnVisibility}
            demographicQuestions={demographicQuestions}
            isDemographicColumnVisible={isDemographicColumnVisible}
            onToggleDemographicColumn={toggleDemographicColumnVisibility}
            onDeleteCustomField={onDeleteCustomField}
            onExport={onExport}
            customColumnData={customColumnData}
            isExportDisabled={isExportDisabled}
          />
        }
      />

      <div className="border rounded-lg">
        <div className="relative w-full overflow-x-auto">
          <Table className="w-full table-auto">
            <TableHeader className="bg-muted/50 [&_th]:text-slate-700">
              <ParticipantsTableHead
                columnVisibility={columnVisibility}
                customColumns={customColumns}
                customColumnVisibility={customColumnVisibility}
                demographicQuestions={demographicQuestions}
                isDemographicColumnVisible={isDemographicColumnVisible}
                activeSortColumn={activeSortColumn}
                activeSortDirection={activeSortDirection}
                onSort={handleSort}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                showActionColumn={showActionColumn}
              />
            </TableHeader>
            <TableBody>
              {hasNoRows ? (
                <TableBodyState
                  colSpan={colSpan}
                  loading={loading}
                  loadingContent={<PageLoader message="Loading" minHeight="min-h-[200px]" />}
                  empty={currentParticipants.length === 0}
                  emptyContent="No participants found"
                />
              ) : (
                currentParticipants.map((participant) => (
                  <ParticipantRow
                    key={participant.id}
                    participant={participant}
                    columnVisibility={columnVisibility}
                    customColumns={customColumns}
                    customColumnVisibility={customColumnVisibility}
                    demographicQuestions={demographicQuestions}
                    isDemographicColumnVisible={isDemographicColumnVisible}
                    editingCell={editingCell}
                    editingValue={editingValue}
                    setEditingValue={setEditingValue}
                    saveEdit={saveEdit}
                    cancelEdit={cancelEdit}
                    startEditing={startEditing}
                    getCustomColumnValue={getCustomColumnValue}
                    onAction={handleParticipantAction}
                    showActionColumn={showActionColumn}
                    nameByOneformaId={participantNameByOneformaUserId}
                    emailByOneformaId={participantEmailByOneformaUserId}
                    earliestSecondaryByPrimaryId={earliestSecondaryByPrimaryOneformaId}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between px-4 py-4 border-t">
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            {totalCount === 0 ? 0 : startIndex + 1} to{' '}
            {Math.min(endIndex, totalCount)} of {totalCount}
          </div>
          <ParticipantsTablePagination
            effectivePage={effectivePage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <ParticipantDetailsDialog
        participant={selectedParticipant}
        isOpen={isDetailsDialogOpen}
        onClose={() => setIsDetailsDialogOpen(false)}
        demographicQuestions={demographicQuestions}
        projectId={projectId ?? null}
        initialTab={detailsInitialTab}
      />

      <ChangeStatusDialog
        participant={selectedParticipant}
        isOpen={isStatusDialogOpen}
        onClose={() => setIsStatusDialogOpen(false)}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
}
